import {
  CloudFunctionResponseCodes,
  ServerTransactionByIdType,
  useCreateRefundByEmployee,
} from '@expane/data'
import { useBusinessModulesSettings } from '@expane/logic/modules'
import { CloseButton, Dialog, Modal, NumberInput, Table } from '@expane/ui'
import { ColumnDef } from '@tanstack/react-table'
import { FC, useMemo } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { SaveButton } from 'widgets/Buttons'
import { useSnackbar } from '@expane/widgets'
import { useConvertNumberToMoneyWithoutSymbol } from '@expane/logic/currency'
import { store } from 'store'
import { observer } from 'mobx-react-lite'
import { TransactionMainInfoWidget } from 'widgets/TransactionMainInfoWidget'
import { getTransactionItemQtyWithUnit } from '@expane/logic/transaction'
import {
  calcItemsToRefundByPaymentProviderTotalPrice,
  extractCardsFromTransactionForRefund,
  extractConsumablesFromTransactionForRefund,
  extractServicesFromTransactionForRefund,
  ItemToRefundByPaymentProvider,
} from '@expane/logic/transaction/withPaymentProvider/refund'
import { roundValue } from '@expane/logic/utils'

interface TransactionRefundDialogLogicProps {
  transactionById: ServerTransactionByIdType
  onSuccess: () => void
  closeDialog: () => void
}

interface RefundFormValues {
  totalRefundPrice: string
}

export const TransactionRefundByPaymentProviderDialogLogic: FC<TransactionRefundDialogLogicProps> =
  observer(({ transactionById, closeDialog, onSuccess }) => {
    const branchId = store.branch.branchId
    const [openSnackbar] = useSnackbar()

    const convertNumberToMoney = useConvertNumberToMoneyWithoutSymbol(branchId)

    const { mutateAsync: createRefundByEmployee } = useCreateRefundByEmployee()

    const itemsToRefund: ItemToRefundByPaymentProvider[] = useMemo(
      () => [
        ...extractCardsFromTransactionForRefund(transactionById),
        ...extractServicesFromTransactionForRefund(transactionById),
        ...extractConsumablesFromTransactionForRefund(transactionById),
      ],
      [transactionById],
    )

    const totalRefundPrice = calcItemsToRefundByPaymentProviderTotalPrice(itemsToRefund)
    const roundedTotalRefundPrice = roundValue(totalRefundPrice)

    const { control, handleSubmit, formState } = useForm<RefundFormValues>({
      defaultValues: {
        totalRefundPrice: roundedTotalRefundPrice.toString(),
      },
    })

    const { t } = useTranslation()

    const { getModuleSetting } = useBusinessModulesSettings()
    const isPaymentEnabled = getModuleSetting('payment')

    const columns = useMemo<ColumnDef<ItemToRefundByPaymentProvider>[]>(
      () => [
        {
          accessorKey: 'position',
          sortingFn: (a, b) => a.original.position.localeCompare(b.original.position),
          header: t('position'),
          size: 420,
        },
        {
          accessorKey: 'quantity',
          header: t('qty'),
          size: 80,
          accessorFn: item => getTransactionItemQtyWithUnit(item.quantity, item.unit, t),
        },
        {
          accessorKey: 'price',
          header: t('price'),
          size: 80,
          accessorFn: item => convertNumberToMoney(item.price),
        },
        {
          accessorKey: 'amount',
          header: t('amount'),
          size: 80,
          accessorFn: item => convertNumberToMoney(item.amount),
        },
      ],
      [convertNumberToMoney, t],
    )

    const submitRefund: SubmitHandler<RefundFormValues> = async ({ totalRefundPrice }) => {
      const totalRefundPriceNumber = Number(totalRefundPrice)

      // Сравниваем с округленной максимальной суммой возврата, а не с полной суммой транзакции
      const partialRefund =
        roundedTotalRefundPrice === totalRefundPriceNumber ? undefined : totalRefundPriceNumber

      if (!transactionById.order?.id) {
        openSnackbar(t('submitError'), 'error')

        return
      }

      const data: {
        orderId: number
        partialRefund?: number
      } = { orderId: transactionById.order.id }

      if (partialRefund) {
        data.partialRefund = partialRefund
      }

      const result = await createRefundByEmployee(data)

      if (result.createRefund?.code === CloudFunctionResponseCodes.successful) {
        openSnackbar(t('refundSuccessfullyCreated'), 'success')
      } else {
        openSnackbar(t('submitError'), 'error')
      }

      closeDialog()
      onSuccess()
    }

    return (
      <>
        <Modal close={closeDialog}>
          <Dialog>
            <Dialog.Title>{t('transaction.creatingRefundByPaymentProvider')}</Dialog.Title>

            <Dialog.Body className="w-144">
              <TransactionMainInfoWidget transactionById={transactionById} />

              {itemsToRefund.length === 0 ? (
                <p className="text-main-color">{t('transaction.noRefunds')}</p>
              ) : (
                <Table columns={columns} data={itemsToRefund} />
              )}

              <div className="mt-3 flex justify-end">
                <Controller
                  control={control}
                  name="totalRefundPrice"
                  rules={{
                    required: true,
                    validate: value => Number(value) <= roundedTotalRefundPrice,
                  }}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <NumberInput
                      label={`${t('cancellationOfBookingPayment.amountToBeReturned')}:`}
                      placeholder={roundedTotalRefundPrice.toString()}
                      value={value}
                      onChange={onChange}
                      disabled={totalRefundPrice === 0}
                      errorMessage={{
                        isShown: Boolean(error),
                        text:
                          error?.type === 'required'
                            ? t('formError.required')
                            : `${t('maximumAmount')}: ${roundedTotalRefundPrice}`,
                      }}
                    />
                  )}
                />
              </div>
            </Dialog.Body>

            <Dialog.Footer>
              <SaveButton
                onClick={handleSubmit(submitRefund)}
                disabled={!isPaymentEnabled || formState.isSubmitting}
                spinner={formState.isSubmitting}
                isCreate
              />

              <CloseButton onClick={closeDialog} />
            </Dialog.Footer>
          </Dialog>
        </Modal>
      </>
    )
  })
